import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SecurityIcon from '@mui/icons-material/Security';
import ChatIcon from '@mui/icons-material/Chat';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AppsIcon from '@mui/icons-material/Apps';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  width: number;
  isMobile: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const navItems = [
  { path: '/requests', label: 'Requests', icon: <QuestionAnswerIcon /> },
  { path: '/metrics', label: 'Org Metrics', icon: <PeopleIcon /> },
  { path: '/onboarding', label: 'Users Status and Metrics', icon: <PersonAddIcon /> },
  { path: '/security', label: 'Cyber Report', icon: <SecurityIcon /> },
  { path: '/agents', label: 'Agents', icon: <SmartToyIcon /> },
  { path: '/apps', label: 'Apps', icon: <AppsIcon /> },
  { path: '/chat', label: 'Chat with Usage', icon: <ChatIcon /> },
  { path: '/features', label: 'Feature Request Extractor', icon: <LightbulbIcon /> },
];

export default function Sidebar({ open, onClose, width, isMobile, collapsed, onToggleCollapse }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo */}
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 1,
          borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
          minHeight: 52,
        }}
      >
        <Box
          component="img"
          src="/pelles_p.png"
          alt="Pelles"
          sx={{
            height: 32,
            width: 'auto',
          }}
        />
      </Box>

      {/* Navigation */}
      <List sx={{ flex: 1, py: 2 }}>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ px: collapsed ? 0.5 : 1 }}>
            <Tooltip title={collapsed ? item.label : ''} placement="right">
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  borderRadius: 1,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  px: collapsed ? 1 : 2,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(139, 92, 246, 0.15)',
                    '&:hover': {
                      backgroundColor: 'rgba(139, 92, 246, 0.25)',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed ? 0 : 40,
                    color: location.pathname === item.path ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: location.pathname === item.path ? 600 : 400,
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      {/* Collapse Toggle */}
      {!isMobile && (
        <Box
          sx={{
            p: 1,
            borderTop: '1px solid rgba(148, 163, 184, 0.1)',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <IconButton size="small" onClick={onToggleCollapse} sx={{ opacity: 0.7 }}>
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={open}
      onClose={onClose}
      sx={{
        width: width,
        flexShrink: 0,
        transition: 'width 0.2s ease',
        '& .MuiDrawer-paper': {
          width: width,
          boxSizing: 'border-box',
          transition: 'width 0.2s ease',
          overflowX: 'hidden',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
